import { Building2, User } from "lucide-react";
import { Sheet, SheetContent, SheetHeader, SheetTitle } from "@/components/ui/sheet";
import type { Contact, Organization, Property } from "@/api/generated/client/models";

interface LinkedDirectoryRecordDrawerProps {
    contact: Contact | null;
    organization: Organization | null;
    property: Property | null;
    onClose: () => void;
}

export function LinkedDirectoryRecordDrawer({
    contact,
    organization,
    property,
    onClose,
}: LinkedDirectoryRecordDrawerProps) {
    const title = contact
        ? `${contact.firstName} ${contact.lastName}`.trim()
        : organization?.name ?? "";
    const Icon = contact ? User : Building2;
    const fields: Array<[string, string | null | undefined]> = contact
        ? [
            ["Type", contact.contactType],
            ["Job title", contact.jobTitle],
            ["Email", contact.email],
            ["Phone", contact.phone],
            ["Notes", contact.notes],
        ]
        : organization
            ? [
                ["Type", organization.orgType],
                ["Phone", organization.phone],
                ["Email", organization.email],
                ["Address", organization.address],
                ["Website", organization.website],
                ["Notes", organization.notes],
            ]
            : [
                ["Street Address", property?.streetAddress],
                ["Unit", property?.unit],
                ["City", property?.city],
                ["State", property?.state],
                ["ZIP code", property?.zipCode],
                ["Country", property?.country],
                ["Notes", property?.notes],
            ];

    return (
        <Sheet open={!!contact || !!organization || !!property} onOpenChange={(open) => !open && onClose()}>
            <SheetContent side="right" className="overflow-y-auto sm:max-w-lg">
                {property ? (
                    <SheetHeader className="sr-only">
                        <SheetTitle>Property details</SheetTitle>
                    </SheetHeader>
                ) : (
                    <SheetHeader className="pb-4">
                        <SheetTitle className="flex min-w-0 items-center gap-2 break-words">
                            <Icon className="h-5 w-5 shrink-0 text-muted-foreground" />
                            <span className="break-words">{title}</span>
                        </SheetTitle>
                    </SheetHeader>
                )}
                <dl className="divide-y">
                    {fields.filter(([, value]) => value?.trim()).map(([label, value]) => (
                        <div key={label} className="grid grid-cols-[6rem_minmax(0,1fr)] gap-3 py-3 text-sm">
                            <dt className="text-muted-foreground">{label}</dt>
                            <dd className="min-w-0 whitespace-pre-wrap break-words">{value}</dd>
                        </div>
                    ))}
                </dl>
            </SheetContent>
        </Sheet>
    );
}